import { collection, doc, type Firestore, getDocs, query, where } from "firebase/firestore";

export type TipoMovimientoRuta = "ingreso" | "salida";

export type MovimientoRutaCompatible = {
  clienteUid?: string;
  descripcion?: string;
  fecha?: any;
  monto?: number | string;
  origen?: string;
  responsable?: string;
  saldoDespues?: number | string;
  tipo?: TipoMovimientoRuta;
  uid?: string;
};

export type RutaConMovimientos = {
  movimientosCount?: number | string;
  saldoInicial?: number | string;
  totalIngresos?: number | string;
  totalSalidas?: number | string;
  ultimoMovimientoAt?: any;
};

export type ActualizacionRutaMovimiento = {
  movimientosCount: number;
  saldoInicial: number;
  totalIngresos: number;
  totalSalidas: number;
  ultimoMovimientoAt: any;
};

export type MetricasMovimientosDelDia = {
  prestamoDelDia: number;
  totalAbonos: number;
};

export type MetricasAdminDelDia = {
  ingresosDelDia: number;
  retirosDelDia: number;
};

export const DESCRIPCIONES_INGRESOS = new Set([
  "abono cliente",
  "abono (admin)",
  "abono editado suma (admin)",
  "Abono Editado (suma)",
  "Ingreso",
  "saldo inicial",
  "Prestamo Editado (resta)",
  "Cliente eliminado S/C",
]);

export const DESCRIPCIONES_SALIDAS = new Set([
  "prestamo",
  "abono editado resta (admin)",
  "Abono Editado (resta)",
  "Prestamo Editado (suma)",
  "Gasto D",
  "Retiro",
]);

const normalizarNumero = (value: number | string | null | undefined): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const crearRangoDelDia = (
  fechaReferencia: Date = new Date(),
): {
  finDia: Date;
  inicioDia: Date;
} => {
  const inicioDia = new Date(fechaReferencia);
  inicioDia.setHours(0, 0, 0, 0);

  const finDia = new Date(inicioDia);
  finDia.setDate(finDia.getDate() + 1);

  return {
    finDia,
    inicioDia,
  };
};

export const calcularSaldoActualRuta = (
  rutaData: RutaConMovimientos | null | undefined,
): number => {
  if (!rutaData) {
    return 0;
  }

  return normalizarNumero(rutaData.saldoInicial);
};

export const obtenerTipoMovimientoRuta = (
  descripcion = "",
  tipo?: TipoMovimientoRuta | null,
): TipoMovimientoRuta => {
  if (tipo === "ingreso" || tipo === "salida") {
    return tipo;
  }

  return DESCRIPCIONES_INGRESOS.has(descripcion) ? "ingreso" : "salida";
};

export const crearMovimientoRutaData = ({
  clienteUid,
  descripcion,
  fecha = new Date(),
  monto,
  origen,
  responsable,
  saldoDespues,
  tipo,
}: {
  clienteUid?: string | null;
  descripcion: string;
  fecha?: Date;
  monto: number;
  origen?: string;
  responsable: string;
  saldoDespues?: number;
  tipo?: TipoMovimientoRuta;
}): MovimientoRutaCompatible => {
  const tipoMovimiento = obtenerTipoMovimientoRuta(descripcion, tipo);

  return {
    ...(clienteUid ? { clienteUid } : {}),
    descripcion,
    fecha,
    monto,
    origen: origen ?? descripcion,
    responsable,
    ...(saldoDespues !== undefined ? { saldoDespues } : {}),
    tipo: tipoMovimiento,
  };
};

const normalizarFechaMovimiento = (fecha: any): Date | null => {
  if (fecha instanceof Date && !Number.isNaN(fecha.getTime())) {
    return fecha;
  }

  if (typeof fecha?.toDate === "function") {
    const fechaConvertida = fecha.toDate();

    return fechaConvertida instanceof Date && !Number.isNaN(fechaConvertida.getTime())
      ? fechaConvertida
      : null;
  }

  const fechaConvertida = new Date(fecha);

  return Number.isNaN(fechaConvertida.getTime()) ? null : fechaConvertida;
};

const esMovimientoTraspasoSalida = (movimiento: MovimientoRutaCompatible): boolean => {
  const descripcion = String(movimiento.descripcion ?? "")
    .trim()
    .toLowerCase();
  const origen = String(movimiento.origen ?? "")
    .trim()
    .toLowerCase();

  return origen === "traspaso_cliente" || descripcion === "traspaso de cliente";
};

const esMovimientoTraspasoEntrada = (movimiento: MovimientoRutaCompatible): boolean => {
  const descripcion = String(movimiento.descripcion ?? "")
    .trim()
    .toLowerCase();
  const origen = String(movimiento.origen ?? "")
    .trim()
    .toLowerCase();

  return (
    origen === "traspaso_cliente_recibido" ||
    descripcion.startsWith('cliente recibido de la ruta "')
  );
};

export const calcularMetricasMovimientosDelDia = (
  movimientos: MovimientoRutaCompatible[],
  fechaReferencia: Date = new Date(),
): MetricasMovimientosDelDia => {
  const fechaClave = fechaReferencia.toDateString();

  return movimientos.reduce<MetricasMovimientosDelDia>(
    (metricas, movimiento) => {
      const fechaMovimiento = normalizarFechaMovimiento(movimiento.fecha);

      if (!fechaMovimiento || fechaMovimiento.toDateString() !== fechaClave) {
        return metricas;
      }

      const monto = normalizarNumero(movimiento.monto);

      if (esMovimientoTraspasoSalida(movimiento)) {
        metricas.totalAbonos += monto;
      }

      if (esMovimientoTraspasoEntrada(movimiento)) {
        metricas.prestamoDelDia += monto;
      }

      return metricas;
    },
    {
      prestamoDelDia: 0,
      totalAbonos: 0,
    },
  );
};

export const calcularIngresosRetirosDelDia = (
  movimientos: MovimientoRutaCompatible[] | undefined,
  fechaReferencia: Date = new Date(),
): MetricasAdminDelDia => {
  if (!movimientos?.length) {
    return {
      ingresosDelDia: 0,
      retirosDelDia: 0,
    };
  }

  const { finDia, inicioDia } = crearRangoDelDia(fechaReferencia);

  return movimientos.reduce<MetricasAdminDelDia>(
    (metricas, movimiento) => {
      const fechaMovimiento = normalizarFechaMovimiento(movimiento.fecha);

      if (!fechaMovimiento || fechaMovimiento < inicioDia || fechaMovimiento >= finDia) {
        return metricas;
      }

      const descripcion = String(movimiento.descripcion ?? "")
        .trim()
        .toLowerCase();
      const monto = normalizarNumero(movimiento.monto);

      if (descripcion === "ingreso") {
        metricas.ingresosDelDia += monto;
      }

      if (descripcion === "retiro") {
        metricas.retirosDelDia += monto;
      }

      return metricas;
    },
    {
      ingresosDelDia: 0,
      retirosDelDia: 0,
    },
  );
};

export const obtenerIngresosRetirosDelDiaDeRuta = async ({
  adminUid,
  db,
  fechaReferencia = new Date(),
  rutaUid,
}: {
  adminUid: string;
  db: Firestore;
  fechaReferencia?: Date;
  rutaUid: string;
}): Promise<MetricasAdminDelDia> => {
  const { finDia, inicioDia } = crearRangoDelDia(fechaReferencia);
  const movimientosSnapshot = await getDocs(
    query(
      crearMovimientosRutaCollectionRef(db, adminUid, rutaUid),
      where("fecha", ">=", inicioDia),
      where("fecha", "<", finDia),
    ),
  );
  const movimientos = movimientosSnapshot.docs.map(
    (movimientoDoc) => movimientoDoc.data() as MovimientoRutaCompatible,
  );

  return calcularIngresosRetirosDelDia(movimientos, fechaReferencia);
};

export const obtenerTotalesRuta = (
  rutaData: RutaConMovimientos | null | undefined,
): {
  movimientosCount: number;
  totalIngresos: number;
  totalSalidas: number;
} => ({
  movimientosCount: normalizarNumero(rutaData?.movimientosCount),
  totalIngresos: normalizarNumero(rutaData?.totalIngresos),
  totalSalidas: normalizarNumero(rutaData?.totalSalidas),
});

export const construirActualizacionRutaConMovimiento = (
  rutaData: RutaConMovimientos | null | undefined,
  movimiento: MovimientoRutaCompatible,
  saldoActualizado: number,
): ActualizacionRutaMovimiento => {
  const { movimientosCount, totalIngresos, totalSalidas } = obtenerTotalesRuta(rutaData);
  const monto = normalizarNumero(movimiento.monto);
  const tipoMovimiento = obtenerTipoMovimientoRuta(movimiento.descripcion ?? "", movimiento.tipo);

  return {
    movimientosCount: movimientosCount + 1,
    saldoInicial: saldoActualizado,
    totalIngresos: tipoMovimiento === "ingreso" ? totalIngresos + monto : totalIngresos,
    totalSalidas: tipoMovimiento === "salida" ? totalSalidas + monto : totalSalidas,
    ultimoMovimientoAt: movimiento.fecha ?? new Date(),
  };
};

export const aplicarMovimientoARutaLocal = <T extends RutaConMovimientos>(
  rutaData: T,
  movimiento: MovimientoRutaCompatible,
  saldoActualizado: number,
): T => ({
  ...rutaData,
  ...construirActualizacionRutaConMovimiento(rutaData, movimiento, saldoActualizado),
});

export const crearMovimientoRutaRef = (db: Firestore, adminUid: string, rutaUid: string) =>
  doc(collection(db, "admin_users", adminUid, "rutas", rutaUid, "movimientos"));

export const crearMovimientosRutaCollectionRef = (
  db: Firestore,
  adminUid: string,
  rutaUid: string,
) => collection(db, "admin_users", adminUid, "rutas", rutaUid, "movimientos");
